import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'

import { firestore } from '../firebase/firebaseConfig'
import { NotificationItem, ProfileRole } from '../types'

export const useNotifications = ({
  userId,
  userRole,
  pageSize = 50,
}: {
  userId?: string
  userRole: ProfileRole | null
  pageSize?: number
}) => {
  const [lastSeen, setLastSeen] = useState<Timestamp | null>(null)
  const [feed, setFeed] = useState<NotificationItem[]>([])

  // 1) escuchar lastSeen
  useEffect(() => {
    if (!userId || userRole !== 'student') return

    const studentRef = doc(firestore, 'students', userId)
    return onSnapshot(studentRef, (snap) => {
      const data = snap.data()
      setLastSeen((data?.lastSeenNotifications as Timestamp) ?? null)
    })
  }, [userId, userRole])

  // 2) SIEMPRE escuchar feed (últimas N), no solo las nuevas
  useEffect(() => {
    if (userRole !== 'student') return

    const notifRef = collection(firestore, 'notifications')
    const q = query(
      notifRef,
      where('roleTarget', '==', 'students'),
      orderBy('createdAt', 'desc'),
      limit(pageSize),
    )

    return onSnapshot(q, (snap) => {
      const items = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<NotificationItem, 'id'>),
      }))
      setFeed(items)
    })
  }, [userRole, pageSize])

  // 3) calcular no leídas comparando contra lastSeen
  const unread = useMemo(() => {
    if (!lastSeen) {
      // si nunca vio nada, considerá todo “nuevo” (o si querés: 0)
      return feed.filter((n) => n.createdAt)
    }
    return feed.filter(
      (n) => n.createdAt && n.createdAt.toMillis() > lastSeen.toMillis(),
    )
  }, [feed, lastSeen])

  const unreadCount = unread.length

  // 4) marcar como visto SIN vaciar el feed
  const markAllAsSeen = useCallback(async () => {
    if (!userId || userRole !== 'student') return

    const newest = feed.find((n) => n.createdAt)?.createdAt
    await updateDoc(doc(firestore, 'students', userId), {
      lastSeenNotifications: newest ?? Timestamp.now(),
    })
  }, [userId, userRole, feed])

  return {
    feed, // siempre visible
    unread, // solo para badge/estilos
    unreadCount,
    lastSeen,
    markAllAsSeen,
  }
}
